import SEO from "@/components/SEO";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import Breadcrumb from "@/components/Breadcrumb";
import InstantQuoteCTA from "@/components/InstantQuoteCTA";
import { Button } from "@/components/ui/button";
import { Phone, ArrowRight, Mail } from "lucide-react";
import { Link } from "react-router-dom";
import militaryHeroImg from "@/assets/military-eagle-flag.jpg";
import militaryHeroVideo from "@/assets/military-eagle-flag-cinematic.mp4.asset.json";

const MilitaryGraduationTransportation = () => {
  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "@id": "https://ricksblackcarservicesantonio.com/military-graduation-transportation#service",
    "name": "Lackland AFB Graduation Transportation",
    "alternateName": "Veteran-Owned Military Graduation Car Service",
    "serviceType": "Military Transportation",
    "provider": {
      "@type": "LocalBusiness",
      "@id": "https://ricksblackcarservicesantonio.com/#business",
      "name": "Rick's Black Car Service",
      "telephone": "+1-210-296-7409",
      "openingHours": "Mo-Su 00:00-23:59"
    },
    "areaServed": {
      "@type": "City",
      "name": "San Antonio",
      "containedInPlace": {
        "@type": "State",
        "name": "Texas"
      }
    },
    "description": "Stress-free transportation for Lackland AFB BMT graduations. Veteran-owned car service with base access experience. Airport pickup to bleachers. 210-296-7409.",
    "image": [
      "https://ricksblackcarservicesantonio.com/og-image.jpg"
    ],
    "offers": {
      "@type": "AggregateOffer",
      "url": "https://ricksblackcarservicesantonio.com/military-graduation-transportation",
      "priceCurrency": "USD",
      "lowPrice": "110",
      "highPrice": "150",
      "offerCount": "1",
      "priceValidUntil": "2026-12-31",
      "availability": "https://schema.org/InStock"
    },
    "priceRange": "$110-$150"
  };

  return (
    <>
      <SEO
        title="Lackland AFB Graduation Transportation | Veteran-Owned | Rick's Black Car"
        description="Stress-free transportation for Lackland AFB BMT graduations. Veteran-owned car service with base access experience. Airport pickup to bleachers. 210-296-7409."
        canonicalPath="/military-graduation-transportation"
        schema={serviceSchema}
        includeBreadcrumbs={false}
      />

      <div className="min-h-screen">
        <Navbar />
        <main className="pt-20">
          <Breadcrumb items={[{ name: "Military Graduation Transportation", url: "/military-graduation-transportation" }]} />
          {/* Hero Section */}
          <section className="relative min-h-[60vh] overflow-hidden flex items-center justify-center">
            <div className="absolute inset-0 z-0">
              <video
                src={militaryHeroVideo.url}
                poster={militaryHeroImg}
                autoPlay
                loop
                muted
                playsInline
                preload="metadata"
                disableRemotePlayback
                aria-label="Veteran-owned transportation for Lackland AFB graduation ceremonies"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-background/30 via-background/40 to-background/60" />
            </div>
            <div className="container mx-auto px-4 relative z-10 text-center">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif font-bold text-foreground mb-6">
                Lackland AFB Graduation Transportation — Veteran-Owned & Base-Access Experienced
              </h1>
              <p className="text-lg md:text-xl text-muted-foreground mb-8 max-w-3xl mx-auto">
                Stress-free transportation for Lackland AFB BMT graduations — from SAT airport pickup to the bleachers and back.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Button className="btn-luxury text-lg px-8 py-6" asChild>
                  <a href="tel:+12102967409">
                    <Phone className="w-5 h-5 mr-2" />
                    Call 210-296-7409
                  </a>
                </Button>
                <Button className="btn-luxury text-lg px-8 py-6" asChild>
                  <a href="https://ricksblackcarservicesantonio.square.site/" target="_blank" rel="noopener noreferrer">
                    Book Online Now
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </a>
                </Button>
              </div>
            </div>
          </section>

          {/* Main Content */}
          <section className="py-20 luxury-gradient">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto">
                <div className="prose prose-lg max-w-none">
                  <p className="text-muted-foreground text-lg leading-relaxed mb-12">
                    Every week, families fly into San Antonio for one of the proudest moments of their lives: watching their Airman graduate from Basic Military Training at Lackland Air Force Base (Joint Base San Antonio). And every week, some of those families lose precious time stuck at gate checkpoints, circling for parking, or waiting on a rideshare driver who's never been on a military installation. There's a better way.
                  </p>

                  <h2 className="text-3xl md:text-4xl font-serif font-bold text-foreground mb-4">
                    Driven by a 22-Year U.S. Army Veteran
                  </h2>
                  <p className="text-muted-foreground text-lg leading-relaxed mb-10">
                    Rick's Black Car Service is owned and operated by a 22-year U.S. Army veteran who has driven graduation families to Lackland for years. That means your chauffeur understands base protocols, gate procedures, and visitor access requirements — and knows exactly where to drop you off and pick you up so you never miss a moment of the ceremony. Loyalty, duty, respect, and punctuality aren't marketing words here; they're how the business runs.
                  </p>

                  <h2 className="text-3xl md:text-4xl font-serif font-bold text-foreground mb-4">
                    From the Airport to the Bleachers
                  </h2>
                  <p className="text-muted-foreground text-lg leading-relaxed mb-10">
                    A typical graduation trip with us looks like this: we track your flight and meet you at <Link to="/airport-transportation" className="text-primary hover:underline">San Antonio International Airport (SAT)</Link>, take you to your hotel, and then handle every leg of graduation week — the Airman's Run, the Coin Ceremony, the Graduation Parade, base liberty outings, and family dinners around town. Families routinely book us for their entire stay because it removes every logistical headache from an emotional, tightly scheduled week.
                  </p>

                  <h2 className="text-3xl md:text-4xl font-serif font-bold text-foreground mb-4">
                    Drop-Off at the Ceremony, Waiting When You Walk Out
                  </h2>
                  <p className="text-muted-foreground text-lg leading-relaxed mb-10">
                    We drop you as close to the ceremony as base rules allow — often right near the bleachers — and we're waiting the moment you walk out. No shuttle lines, no long walks in the Texas heat with grandparents and kids in tow, no post-ceremony parking lot gridlock.
                  </p>

                  <h2 className="text-3xl md:text-4xl font-serif font-bold text-foreground mb-4">
                    See San Antonio During Base Liberty
                  </h2>
                  <p className="text-muted-foreground text-lg leading-relaxed mb-10">
                    When your new Airman earns town liberty, make the most of it. Your chauffeur doubles as a knowledgeable local guide with genuine recommendations for the Riverwalk, the missions, the Pearl, and family-friendly restaurants — so a short liberty window becomes a great memory instead of a scramble.
                  </p>

                  <h2 className="text-3xl md:text-4xl font-serif font-bold text-foreground mb-4">
                    Also Serving Fort Sam Houston and Randolph
                  </h2>
                  <p className="text-muted-foreground text-lg leading-relaxed mb-12">
                    We support graduations, ceremonies, and family visits across all of Joint Base San Antonio, including Fort Sam Houston and Randolph AFB, plus military family airport transfers year-round.
                  </p>
                </div>

                {/* Bottom CTA */}
                <div className="text-center py-12 border-t border-border/50">
                  <p className="text-xl md:text-2xl text-foreground font-semibold mb-6">
                    Graduation week fills up fast — reserve your dates now. Call or text 210-296-7409, or email ricksblackcarservice@outlook.com. From one military family to another: we'll take care of you.
                  </p>
                  <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                    <Button className="btn-luxury text-lg px-8 py-6" asChild>
                      <a href="tel:+12102967409">
                        <Phone className="w-5 h-5 mr-2" />
                        Call 210-296-7409
                      </a>
                    </Button>
                    <Button className="btn-luxury text-lg px-8 py-6" asChild>
                      <a href="mailto:ricksblackcarservice@outlook.com">
                        <Mail className="w-5 h-5 mr-2" />
                        Email Us
                      </a>
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <InstantQuoteCTA />
        </main>
        <Footer />
      </div>
    </>
  );
};

export default MilitaryGraduationTransportation;
